import { useState } from "react";

import { useAccount } from "@/lib/useAccount";
import { useSaveState } from "@/lib/useSaveState";

import { StatusBanner } from "./ui";

import {
  MAX_DIFFICULTY,
  MAX_QUALITY,
  MIN_DIFFICULTY,
  MIN_QUALITY,
  PUZZLE_CHARACTERS,
  type MemberRating,
  type Puzzle,
  type PuzzleCharacter,
  type RatingDraft,
} from "@/lib/puzzlesApi";

const CHARACTER_LABELS: Record<PuzzleCharacter, string> = {
  educational: "Educational",
  exercise: "Exercise",
  demon: "Demon",
  fun: "Fun",
};

/** The club's own descriptions, from the Info sheet that proposed these. */
const CHARACTER_HINTS: Record<PuzzleCharacter, string> = {
  educational: "Useful, practical, quick to see and use",
  exercise: "The ones that make us feel good",
  demon: "Torture",
  fun: "Silly ones",
};

/**
 * "Solved it" has to be answerable three ways, not two.
 *
 * A plain checkbox can only distinguish yes from *unticked*, and unticked is
 * not the same claim as "no". The API and the column are `boolean | null`, and
 * `solved_rate` is an AVG that skips NULLs — so if the UI never sends `false`,
 * the average is taken over a set of nothing but 1.0 and every puzzle anyone
 * has rated reads as 100% solved. Clicking the selected choice again clears it
 * back to "did not say", the same way quality and character do.
 */
const SOLVED_CHOICES: ReadonlyArray<{ value: boolean; label: string }> = [
  { value: true, label: "Solved it" },
  { value: false, label: "Did not" },
];

const DIFFICULTIES = Array.from(
  { length: MAX_DIFFICULTY - MIN_DIFFICULTY + 1 },
  (_, index) => MIN_DIFFICULTY + index,
);
const QUALITIES = Array.from(
  { length: MAX_QUALITY - MIN_QUALITY + 1 },
  (_, index) => MIN_QUALITY + index,
);

interface PuzzleRatingFormProps {
  readonly puzzle: Puzzle;
  readonly onSubmit: (draft: RatingDraft) => Promise<Puzzle>;
  readonly onRemove: () => Promise<void>;
}

function initial(rating: MemberRating | null) {
  return {
    difficulty: rating?.difficulty ?? null,
    quality: rating?.quality ?? null,
    character: rating?.character ?? null,
    solved: rating?.solved ?? null,
  };
}

/**
 * Rate one puzzle.
 *
 * Only difficulty is required. The archive's own numbers were, by their
 * author's admission, an inconsistent "vibe based" scale — so the one thing
 * worth insisting on is a second opinion about difficulty, and everything else
 * is optional rather than a barrier to giving it.
 */
export function PuzzleRatingForm({ puzzle, onSubmit, onRemove }: PuzzleRatingFormProps) {
  const [draft, setDraft] = useState(() => initial(puzzle.my_rating));
  // PuzzleCard is keyed by puzzle id, so signing in as someone else re-renders
  // this same instance with a new rating rather than remounting it. Without
  // re-seeding, the form would keep showing — and on Update, submit — whatever
  // the previous member had selected. Comparing identity is enough: the list
  // hands back a fresh object whenever it refetches or a save returns.
  const [seededFrom, setSeededFrom] = useState(puzzle.my_rating);
  if (seededFrom !== puzzle.my_rating) {
    setSeededFrom(puzzle.my_rating);
    setDraft(initial(puzzle.my_rating));
  }
  // Shared with the admin panels — notably for its in-flight guard, so a
  // double-clicked submit sends one request rather than two.
  //
  // A member session lasts thirty days, so a tab left open across one is the
  // ordinary case, not an exotic one; an officer changing their password revokes
  // theirs outright. Handing `clearSession` over is what turns the resulting 401
  // into signed-out UI: without it the banner said the session had expired while
  // the rest of the page went on offering Rate buttons and an "Signed in as"
  // line, and every further click failed the same way with no route back to the
  // sign-in form.
  const { clearSession } = useAccount();
  const { saving, message, tone, run } = useSaveState(clearSession);
  const [localError, setLocalError] = useState<string | null>(null);

  const busy = saving;
  const error = localError ?? (tone === "error" ? message : null);

  const patch = (changes: Partial<typeof draft>) =>
    setDraft((current) => ({ ...current, ...changes }));

  const save = async () => {
    if (draft.difficulty === null) {
      setLocalError("Pick a difficulty first.");
      return;
    }
    setLocalError(null);
    const updated = await run(() =>
      onSubmit({
        difficulty: draft.difficulty as number,
        quality: draft.quality,
        character: draft.character,
        solved: draft.solved,
      }),
    );
    if (updated) setDraft(initial(updated.my_rating));
  };

  const remove = async () => {
    setLocalError(null);
    const done = await run(async () => {
      await onRemove();
      return true;
    });
    if (done) setDraft(initial(null));
  };

  return (
    <div className="mt-5 border-t-2 border-ink pt-4">
      <fieldset className="mb-4">
        <legend className="label mb-2">
          Difficulty <span className="opacity-60">(required)</span>
        </legend>
        <div className="flex flex-wrap gap-1.5">
          {DIFFICULTIES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={draft.difficulty === value}
              onClick={() => patch({ difficulty: value })}
              className={`w-9 border-2 border-ink py-1.5 font-mono text-sm transition-colors
                duration-100 ${
                  draft.difficulty === value
                    ? "bg-ink text-field"
                    : "bg-paper hover:bg-ink hover:text-field"
                }`}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <legend className="label mb-2">Quality</legend>
        <div className="flex flex-wrap gap-1.5">
          {QUALITIES.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={draft.quality === value}
              onClick={() => patch({ quality: draft.quality === value ? null : value })}
              className={`w-9 border-2 border-ink py-1.5 font-mono text-sm transition-colors
                duration-100 ${
                  draft.quality === value
                    ? "bg-piece-s text-ink"
                    : "bg-paper hover:bg-ink hover:text-field"
                }`}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <legend className="label mb-2">What kind of puzzle is it?</legend>
        <div className="flex flex-wrap gap-2">
          {PUZZLE_CHARACTERS.map((value) => (
            <button
              key={value}
              type="button"
              title={CHARACTER_HINTS[value]}
              aria-pressed={draft.character === value}
              onClick={() => patch({ character: draft.character === value ? null : value })}
              className={`border-2 border-ink px-2.5 py-1.5 text-xs font-bold uppercase
                transition-colors duration-100 ${
                  draft.character === value
                    ? "bg-piece-t text-ink"
                    : "bg-paper hover:bg-ink hover:text-field"
                }`}
            >
              {CHARACTER_LABELS[value]}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-4">
        <legend className="label mb-2">Did you solve it?</legend>
        <div className="flex flex-wrap gap-2">
          {SOLVED_CHOICES.map(({ value, label }) => (
            <button
              key={label}
              type="button"
              aria-pressed={draft.solved === value}
              onClick={() => patch({ solved: draft.solved === value ? null : value })}
              className={`border-2 border-ink px-2.5 py-1.5 text-xs font-bold uppercase
                transition-colors duration-100 ${
                  draft.solved === value
                    ? "bg-piece-s text-ink"
                    : "bg-paper hover:bg-ink hover:text-field"
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mb-3">
        <StatusBanner tone="error" message={error} prefix={null} />
      </div>

      <div className="flex flex-wrap gap-3">
        <button type="button" className="btn px-4 py-2 text-sm" onClick={save} disabled={busy}>
          {busy ? "Saving…" : puzzle.my_rating ? "Update rating" : "Submit rating"}
        </button>
        {puzzle.my_rating ? (
          <button
            type="button"
            className="btn btn--ghost px-4 py-2 text-sm"
            onClick={remove}
            disabled={busy}
          >
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}
